import type { BookingGarment } from '@/api/bookings';
import { useUnitMedia } from '@/api/masters';
import { MediaViewer } from '@/components/MediaViewer';

/**
 * A garment's own pictures, opened from a booking.
 *
 * <p>
 * <strong>Fetched when opened rather than carried on the booking.</strong> `BookingGarmentDto`
 * already brings one thumbnail per line, which is what the rows render; bringing every photograph of
 * every garment would put a wedding party's whole gallery — eleven garments at up to twenty files
 * each — into the response that the register also uses, for a viewer nobody has opened.
 * </p>
 *
 * <p>
 * <strong>It renders nothing while loading and nothing when empty.</strong> The thumbnail that
 * opened it is already disabled when there is no picture, so an empty viewer here would mean the
 * gallery is genuinely bare — the design's photograph is what the row was showing — and a modal
 * saying so is worse than the click doing nothing.
 * </p>
 */
export function GarmentPhotos({
  garment,
  onClose,
}: {
  garment: BookingGarment;
  onClose: () => void;
}) {
  const media = useUnitMedia(garment.unitId);

  const rows = media.data ?? [];

  if (rows.length === 0) {
    return null;
  }

  return (
    <MediaViewer
      items={rows.map((row) => ({
        id: row.id,
        url: row.url,
        mediaType: row.mediaType,
        fileName: row.fileName,
        caption: row.caption,
        note: row.isPrimary ? 'Main picture' : null,
      }))}
      title={`${garment.itemName} · ${garment.serialCode}`}
      onClose={onClose}
    />
  );
}
